import * as React from 'react';
import styled from 'styled-components';

const fallbackActivityMessageIcon = 'https://anywhereexpert-static.mysoluto.com/media/fallbackActivityImage.png';
const fallbackActivityMessageIconDarkMode =
  'https://anywhereexpert-static.mysoluto.com/media/fallbackActivityImageDarkMode.png';

const Container = styled.div<{borderColor: string}>`
  min-width: 250px;
  max-width: 348px;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 8px;
  padding: 24px;
  border: 2px solid ${({borderColor}) => borderColor};
`;

const SectionIndicationContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Top = styled.div`
  display: flex;
  margin-bottom: 6px;
  justify-content: space-between;
`;

const Image = styled.img`
  position: absolute;
  right: 16px;
  top: 16px;
  height: 56px;
  width: 56px;
  object-fit: contain;
`;

const Icon = styled.img`
  height: 12px;
  width: 12px;
  object-fit: contain;
  margin-right: 6px;
  margin-top: -2px;
`;

const Title = styled.span<{color: string}>`
  max-width: 200px;
  display: block;
  line-height: 24px;
  font-size: 20px;
  font-weight: 200;
  margin-bottom: 12px;
  white-space: pre-line;
  color: ${({color}) => color};
`;

const IndicationText = styled.span<{color: string}>`
  line-height: 18px;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  color: ${({color}) => color};
`;

const Text = styled.span<{color: string}>`
  display: block;
  color: ${({color}) => color};
  font-size: 14px;
  line-height: 21px;
  white-space: pre-line;
`;

type Props = {
  isDarkMode: boolean;
  title: string;
  image: string;
  imageDarkMode: string;
  text: string;
  sectionIndicationText?: string;
  sectionIndicationIcon?: string;
  sectionIndicationIconDarkMode?: string;
  secondaryText?: string;
};

const useGetColors = (isDarkMode: boolean) =>
  React.useMemo(
    () => ({
      textColor: isDarkMode ? '#FFF' : '#000',
      titleColor: isDarkMode ? '#FFF' : '#000',
      borderColor: isDarkMode ? '#6E767D' : '#E6E6EB',
      sectionIndicationTextColor: isDarkMode ? '#FFF' : '#000',
      secondaryTextColor: isDarkMode ? '#A5AAAF' : '#6E767D',
    }),
    [isDarkMode]
  );

const ActivityMessage = ({
  isDarkMode = false,
  title,
  image,
  sectionIndicationText,
  sectionIndicationIcon,
  secondaryText,
  sectionIndicationIconDarkMode,
  imageDarkMode,
  text,
}: Props) => {
  const {borderColor, textColor, secondaryTextColor, sectionIndicationTextColor, titleColor} = useGetColors(isDarkMode);
  const imageSource = React.useMemo(() => (isDarkMode ? imageDarkMode : image) || image, [
    image,
    imageDarkMode,
    isDarkMode,
  ]);
  const fallbackSectionIndicationImages = React.useMemo(
    () => (isDarkMode ? fallbackActivityMessageIconDarkMode : fallbackActivityMessageIcon),
    [isDarkMode]
  );

  const sectionIndicationIconSource = React.useMemo(
    () => (isDarkMode ? sectionIndicationIconDarkMode : sectionIndicationIcon) || fallbackSectionIndicationImages,
    [sectionIndicationIcon, sectionIndicationIconDarkMode, isDarkMode, fallbackSectionIndicationImages]
  );

  const shouldShowSectionIndicationText = React.useMemo(() => !!sectionIndicationText || !!title, [
    sectionIndicationText,
    title,
  ]);

  return (
    <Container borderColor={borderColor}>
      <Top>
        {shouldShowSectionIndicationText && (
          <SectionIndicationContainer>
            <Icon aria-hidden={true} src={sectionIndicationIconSource} />
            <IndicationText color={sectionIndicationTextColor}>
              {sectionIndicationText || 'Account Activity'}
            </IndicationText>
          </SectionIndicationContainer>
        )}
        {imageSource && <Image aria-hidden={true} src={imageSource} />}
      </Top>
      <Title color={titleColor}>{title || 'Recent account activity:'}</Title>
      <Text color={textColor}>{text}</Text>
      {secondaryText && <Text color={secondaryTextColor}>{secondaryText}</Text>}
    </Container>
  );
};

export default ActivityMessage;
